import { api } from "@/lib/api";
import type { PaymentMethod } from "@/types/ipdBilling";

const base = "/api/pharmacy";

/** OT issue/finalize runs stock allocation per drug; default axios timeout (30s) aborts large kits. Match upstream proxy headroom. */
const OT_LONG_RUNNING_MS = 180_000;

export type IPDPostDischargePharmaRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export interface IPDPostDischargePharmaRequest {
  id: string;
  status: IPDPostDischargePharmaRequestStatus;
  reason: string;
  adminNote?: string | null;
  createdAt: string;
  reviewedAt?: string | null;
  requestedBy?: { id: string; name: string };
  reviewedBy?: { id: string; name: string } | null;
  admission?: {
    queue?: {
      ipdNumber?: string;
      patient?: { name?: string; uhid?: string | null };
    };
  };
}

export interface PrescriptionQueueItem {
  id: string;
  prescriptionNumber: string;
  status: string;
  patientId: string;
  patient?: { id: string; name: string; patientUniqueId?: string; uhid?: string };
  doctor?: { id: string; name: string; specialisation?: string };
  admission?: {
    id: string;
    admissionNumber: string;
    bed?: { id: string; bedNumber: string; ward?: { id: string; name: string } };
  };
  items: Array<{
    id: string;
    drugId: string;
    quantity: number;
    dispensedQuantity: number;
    drug?: { id: string; name: string; strength?: string; form?: string; unit?: string; hsnCode?: string; gstRate?: number };
  }>;
  validUntil: string;
  createdAt: string;
}

export interface PrescriptionDetail extends PrescriptionQueueItem {
  diagnosis?: string;
  notes?: string;
  instructions?: string;
  validFrom: string;
  items: Array<{
    id: string;
    prescriptionId: string;
    drugId: string;
    dosage: string;
    frequency: string;
    duration: string;
    quantity: number;
    unit: string;
    route?: string;
    timing?: string;
    specialInstructions?: string;
    status: string;
    dispensedQuantity: number;
    drug: { id: string; name: string; strength?: string; form?: string; unit?: string; hsnCode?: string; gstRate?: number };
  }>;
}

export interface EditablePurchaseStockIn {
  ledgerId: string | null;
  /** Grace window: batch fields only (no purchase ledger / past stock). */
  batchDirectEdit?: boolean;
  /** Purchase ledger line qty */
  quantity: number;
  /** Current batch on-hand (edit dialog sets this total) */
  batchOnHand?: number;
  purchaseRate: number | null;
  gstRate: number | null;
  mrp: number | null;
  expiryDate: string | null;
  batchNo: string | null;
  vendorId: string | null;
  vendorInvoiceNumber: string | null;
  notes: string | null;
  createdAt: string;
  editableUntil: string;
  graceActive?: boolean;
}

export interface PharmaStockInEditGraceStatus {
  active: boolean;
  until: string | null;
  source: "env" | "hospital" | null;
}

export interface BatchOption {
  batchId: string;
  batchNo: string;
  expiryDate: string;
  mrp: number;
  purchaseRate?: number | null;
  gstRate: number | null;
  available: number;
  editableStockIn?: EditablePurchaseStockIn | null;
}

export interface InvoiceStockInLine {
  ledgerId: string;
  drugId: string;
  drugName: string;
  strength: string | null;
  batchNo: string | null;
  quantity: number;
  purchaseRate: number | null;
  createdAt: string;
  editableUntil: string;
  editable: boolean;
}

export type StockRiskUrgency = "CRITICAL" | "HIGH" | "WATCH";

export interface StockRiskRow {
  drugId: string;
  name: string;
  strength: string | null;
  form: string | null;
  unit: string;
  onHand: number;
  reorderLevel: number;
  minimumStock: number;
  netIssuedLookback: number;
  avgDailyUsage: number;
  daysToEmpty: number | null;
  estimatedEmptyDate: string | null;
  urgency: StockRiskUrgency;
  reason: string;
}

export interface StockRiskResponse {
  lookbackDays: number;
  maxDaysToEmpty: number;
  asOf: string;
  items: StockRiskRow[];
}

export type ExpiryRiskBucketKey = '0_30' | '31_60' | '61_90' | '91_180';

export interface ExpiryRiskRow {
  batchId: string;
  drugId: string;
  name: string;
  strength: string | null;
  form: string | null;
  unit: string;
  batchNo: string;
  location: string;
  quantity: number;
  mrp: number;
  expiryDate: string;
  daysUntilExpiry: number;
  bucket: ExpiryRiskBucketKey;
}

export interface ExpiryRiskBucketSummary {
  key: ExpiryRiskBucketKey;
  label: string;
  maxDays: number;
  batchCount: number;
  totalQty: number;
}

export interface ExpiryRiskResponse {
  asOf: string;
  horizonDays: number;
  buckets: ExpiryRiskBucketSummary[];
  items: ExpiryRiskRow[];
}

export const pharmacyApi = {
  getPrescriptionQueue: (params?: { type?: "OPD" | "IPD"; wardId?: string; admissionId?: string; patientId?: string }) =>
    api.get<{ data: PrescriptionQueueItem[] }>(`${base}/prescription-queue`, { params }),

  getPrescription: (id: string) =>
    api.get<{ data: PrescriptionDetail }>(`${base}/prescription/${id}`),

  getBatchesForDrug: (drugId: string, quantity?: number) =>
    api.get<{ data: BatchOption[] }>(`${base}/drugs/${drugId}/batches`, { params: { quantity: quantity ?? 1 } }),

  getAllBatchesForDrug: (
    drugId: string,
    params?: { includeExpired?: boolean; includeZero?: boolean; location?: "MAIN" | "OT"; includeEditable?: boolean }
  ) =>
    api.get<{ data: BatchOption[] }>(`${base}/drugs/${drugId}/batches`, {
      params: { all: true, ...(params ?? {}), ...(params?.includeEditable ? { includeEditable: true } : {}) },
    }),

  getStock: (drugId: string) =>
    api.get<{ data: { drugId: string; quantity: number } }>(`${base}/stock/${drugId}`),

  /** Medicines about to finish — on-hand vs reorder floor + days-to-empty from issue velocity. */
  getStockRisk: (params?: { lookbackDays?: number; maxDaysToEmpty?: number; limit?: number }) =>
    api.get<{ data: StockRiskResponse }>(`${base}/stock-risk`, { params }),

  /** Batches expiring within 30 / 60 / 90 / 180 days (qty > 0). */
  getExpiryRisk: (params?: { horizonDays?: number; limit?: number }) =>
    api.get<{ data: ExpiryRiskResponse }>(`${base}/expiry-risk`, { params }),

  exportStockCsv: (drugId: string, params?: { includeExpired?: boolean; includeZero?: boolean; location?: "MAIN" | "OT" }) =>
    api.get<Blob>(`${base}/stock/${drugId}/export-csv`, {
      params: { ...(params ?? {}) },
      responseType: "blob",
    }),

  exportFullInventoryCsv: (params?: { asOf?: string; includeExpired?: boolean; includeZero?: boolean; location?: "MAIN" | "OT" }) =>
    api.get<Blob>(`${base}/stock/export-csv`, {
      params: { ...(params ?? {}) },
      responseType: "blob",
    }),

  issueOPD: (body: { prescriptionId: string; patientId: string; appointmentId?: string }) =>
    api.post<{ data: unknown }>(`${base}/issue-opd`, body),

  issueIPD: (body: { prescriptionId: string }) =>
    api.post<{ data: PrescriptionDetail }>(`${base}/issue-ipd`, body),

  issueIPDDirect: (body: { admissionId: string; items: { drugId: string; quantity: number }[] }) =>
    api.post<{ data: { admissionId: string; pharmacyIssueIds: string[] } }>(`${base}/ipd-pharma/issue-direct`, body),

  settleIPDPharmaAtPharmacy: (body: {
    admissionId: string;
    chargeableIds: string[];
    paymentMethod: PaymentMethod;
  }) =>
    api.post<{
      data: {
        totalCollected: number;
        chargeableIds: string[];
        channel: string;
        receiptBillId?: string | null;
      };
    }>(`${base}/ipd-pharma/settle`, body, { timeout: OT_LONG_RUNNING_MS }),

  removeIPDPharmaLine: (chargeableId: string) =>
    api.delete<{ data: unknown }>(`${base}/ipd-pharma/line/${chargeableId}`),

  reduceIPDPharmaLineQuantity: (chargeableId: string, quantity: number) =>
    api.patch<{ data: unknown }>(`${base}/ipd-pharma/line/${chargeableId}`, { quantity }),

  listDischargedPackagedAdmissions: (search?: string) =>
    api.get<{
      data: Array<{
        admissionId: string;
        ipdNumber: string;
        patientName: string;
        uhid: string | null;
        dischargeDate: string;
        access: 'LOCKED' | 'PENDING' | 'UNLOCKED';
        unlockedUntil: string | null;
        pendingRequestId: string | null;
      }>;
    }>(`${base}/ipd-pharma/discharged-packaged`, { params: search ? { search } : undefined }),

  getPostDischargePharmaAccess: (admissionId: string) =>
    api.get<{
      data: {
        admissionDischarged: boolean;
        isFullyInclusivePackage: boolean;
        locked: boolean;
        reason?: string;
        unlockedUntil: string | null;
        pendingRequest: { id: string; reason: string; createdAt: string } | null;
      };
    }>(`${base}/ipd-pharma/post-discharge-access/${admissionId}`),

  createPostDischargePharmaRequest: (body: { admissionId: string; reason: string }) =>
    api.post<{ data: unknown }>(`${base}/ipd-pharma/post-discharge-requests`, body),

  listPostDischargePharmaRequests: (opts?: {
    status?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'ALL';
    limit?: number;
  }) =>
    api.get<{ data: IPDPostDischargePharmaRequest[] }>(`${base}/ipd-pharma/post-discharge-requests`, {
      params: {
        status: opts?.status ?? 'PENDING',
        ...(opts?.limit != null ? { limit: opts.limit } : {}),
      },
    }),

  approvePostDischargePharmaRequest: (requestId: string, adminNote?: string) =>
    api.post<{ data: unknown }>(`${base}/ipd-pharma/post-discharge-requests/${requestId}/approve`, {
      adminNote,
    }),

  rejectPostDischargePharmaRequest: (requestId: string, adminNote?: string) =>
    api.post<{ data: unknown }>(`${base}/ipd-pharma/post-discharge-requests/${requestId}/reject`, {
      adminNote,
    }),

  createReturn: (body: { pharmacyIssueId: string; quantityReturned: number; reason?: string; creditNoteId?: string }) =>
    api.post<{ data: unknown }>(`${base}/return`, body),

  getReturnBillBySuffix: (billSuffix: string) =>
    api.get<{ data: any }>(`${base}/returns/bill`, { params: { billSuffix } }),

  returnByBill: (body: { billId: string; returns: Array<{ pharmacyIssueId: string; quantityReturned: number }>; reason?: string; refundAmount?: number; refundMethod?: string }) =>
    api.post<{ data: any }>(`${base}/returns/bill`, body),

  getReturnHistory: (params?: { from?: string; to?: string }) =>
    api.get<{ data: any }>(`${base}/returns/history`, { params }),

  getAudit: (params?: { drugId?: string; batchId?: string; referenceType?: string; limit?: number }) =>
    api.get<{ data: unknown[] }>(`${base}/audit`, { params }),

  stockIn: (body: {
    drugId: string;
    batchId?: string;
    batchNo?: string;
    expiryDate?: string;
    mrp?: number;
    purchaseRate: number;
    gstRate?: number;
    quantity: number;
    notes?: string;
    vendorId: string;
    vendorInvoiceNumber: string;
    vendorInvoiceDate?: string;
  }) =>
    api.post<{ data: unknown }>(`${base}/stock-in`, body),

  getStockInByInvoice: (params: { vendorId: string; vendorInvoiceNumber: string }) =>
    api.get<{ data: { lines: InvoiceStockInLine[]; count: number } }>(`${base}/stock-in/by-invoice`, { params }),

  getStockInEditGrace: (params?: { hospitalId?: string }) =>
    api.get<{ data: PharmaStockInEditGraceStatus }>(`${base}/stock-in/edit-grace`, { params }),

  enableStockInEditGrace: (body?: { hours?: number }, params?: { hospitalId?: string }) =>
    api.post<{ data: PharmaStockInEditGraceStatus & { until: string } }>(
      `${base}/stock-in/edit-grace`,
      body ?? {},
      { params }
    ),

  updatePurchaseStockIn: (
    ledgerId: string,
    body: {
      quantity: number;
      purchaseRate: number;
      gstRate?: number | null;
      mrp: number;
      expiryDate: string;
      notes?: string | null;
    }
  ) => api.patch<{ data: unknown }>(`${base}/stock-in/${ledgerId}`, body),

  updateBatchStockCorrection: (
    batchId: string,
    body: {
      quantity: number;
      purchaseRate: number;
      gstRate?: number | null;
      mrp: number;
      expiryDate: string;
      notes?: string | null;
    }
  ) => api.patch<{ data: unknown }>(`${base}/batches/${batchId}/stock-correction`, body),

  // Vendors
  listVendors: (params?: { hospitalId?: string }) =>
    api.get<{ data: any[] }>(`${base}/vendors`, { params }),
  resetVendorOutstandingBalances: (
    body: { confirm: "RESET_OUTSTANDING" },
    params?: { hospitalId?: string }
  ) =>
    api.post<{ data: { vendorsSettled: number; settled: Array<{ vendorId: string; name: string; amount: number }> } }>(
      `${base}/vendors/reset-outstanding-balances`,
      body,
      { params }
    ),
  createVendor: (
    body: { name: string; phone: string; address?: string; gstNo?: string; email?: string },
    params?: { hospitalId?: string }
  ) => api.post<{ data: any }>(`${base}/vendors`, body, { params }),
  getVendorDetail: (vendorId: string, params?: { hospitalId?: string }) =>
    api.get<{ data: any }>(`${base}/vendors/${vendorId}`, { params }),
  updateVendor: (
    vendorId: string,
    body: { name?: string; phone?: string; address?: string; gstNo?: string; email?: string },
    params?: { hospitalId?: string }
  ) => api.patch<{ data: any }>(`${base}/vendors/${vendorId}`, body, { params }),
  deleteVendor: (vendorId: string, params?: { hospitalId?: string }) =>
    api.delete<{ data: any }>(`${base}/vendors/${vendorId}`, { params }),
  getVendorLedgerStatement: (vendorId: string, params?: { hospitalId?: string }) =>
    api.get<{ data: { ledger: any; rows: any[] } }>(`${base}/vendors/${vendorId}/ledger-statement`, { params }),
  getVendorInvoices: (vendorId: string, params?: { hospitalId?: string }) =>
    api.get<{ data: any[] }>(`${base}/vendors/${vendorId}/invoices`, { params }),
  createVendorInvoice: (vendorId: string, formData: FormData, params?: { hospitalId?: string }) =>
    api.post<{ data: any }>(`${base}/vendors/${vendorId}/invoices`, formData, { params }),
  updateVendorInvoice: (
    vendorId: string,
    invoiceId: string,
    body: { totalAmount: number },
    params?: { hospitalId?: string }
  ) =>
    api.patch<{ data: any }>(`${base}/vendors/${vendorId}/invoices/${invoiceId}`, body, { params }),
  getVendorPayments: (vendorId: string, params?: { hospitalId?: string }) =>
    api.get<{ data: any[] }>(`${base}/vendors/${vendorId}/payments`, { params }),
  createVendorPayment: (
    vendorId: string,
    body: { amount: number; paymentMethod: string; paymentDate?: string; notes?: string },
    params?: { hospitalId?: string }
  ) => api.post<{ data: any }>(`${base}/vendors/${vendorId}/payments`, body, { params }),

  // OT Pharmacy
  otDashboard: () => api.get<{ data: any }>(`${base}/ot/dashboard`),
  listOTMinStock: () => api.get<{ data: any[] }>(`${base}/ot/min-stock`),
  upsertOTMinStock: (body: { drugId: string; minQty: number; reorderQty: number }) =>
    api.put<{ data: any }>(`${base}/ot/min-stock`, body),
  transferToOT: (body: { items: Array<{ fromBatchId: string; quantity: number }>; notes?: string }) =>
    api.post<{ data: any }>(`${base}/ot/transfer`, body, { timeout: OT_LONG_RUNNING_MS }),
  /** Queued on server (202): polls until kit is ready — avoids gateway 504 on slow stock allocation. */
  issueOTSurgeryKit: async (body: {
    admissionId: string;
    surgeryDate: string;
    procedureName?: string;
    surgeonId?: string;
    notes?: string;
    items: Array<{ drugId: string; batchId?: string; quantity: number }>;
  }) => {
    const res = await api.post<{ message?: string; data?: { jobId?: string } }>(`${base}/ot/surgery-kit/issue`, body, {
      timeout: 60_000,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
    });
    if (res.status !== 202) {
      return res;
    }
    const jobId = res.data?.data?.jobId;
    if (!jobId) {
      throw new Error("Issue accepted but job id missing");
    }
    const deadline = Date.now() + OT_LONG_RUNNING_MS;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 750));
      const poll = await api.get<{
        message?: string;
        data?: { status?: string; kit?: unknown; errorMessage?: string | null };
      }>(`${base}/ot/surgery-kit/issue-jobs/${jobId}`, { timeout: 45_000 });
      const d = poll.data?.data;
      if (!d?.status) continue;
      if (d.status === "SUCCEEDED" && d.kit) {
        return {
          ...poll,
          data: { message: "OT surgery kit issued", data: d.kit },
        } as typeof res;
      }
      if (d.status === "FAILED") {
        const msg = d.errorMessage || "Failed to issue kit";
        const err = new Error(msg) as Error & { response?: { data?: { message?: string } } };
        err.response = { data: { message: msg } };
        throw err;
      }
    }
    throw new Error("Kit issue is taking too long. Check OT dashboard or try again.");
  },

  /** Load kit after refresh — same admission + surgery date as issue. */
  getOTSurgeryKitByAdmission: (params: { admissionId: string; surgeryDate: string }) =>
    api.get<{ message: string; data: Record<string, unknown> | null }>(`${base}/ot/surgery-kit/by-admission`, {
      params,
    }),

  returnOTSurgeryKit: (kitId: string, body: { returns: Array<{ itemId: string; quantity: number }> }) =>
    api.post<{ data: any }>(`${base}/ot/surgery-kit/${kitId}/return`, body, { timeout: OT_LONG_RUNNING_MS }),
  finalizeOTSurgeryKit: (kitId: string) =>
    api.post<{ data: any }>(`${base}/ot/surgery-kit/${kitId}/finalize`, {}, { timeout: OT_LONG_RUNNING_MS }),
  finalizeOTSurgeryKitWithActuals: (kitId: string, body: { actuals: Array<{ drugId: string; quantity: number }> }) =>
    api.post<{ data: any }>(`${base}/ot/surgery-kit/${kitId}/finalize`, body, { timeout: OT_LONG_RUNNING_MS }),
  uploadOTSurgeryKitAttachment: (kitId: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post<{ data: any }>(`${base}/ot/surgery-kit/${kitId}/attachment`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: OT_LONG_RUNNING_MS,
    });
  },
  exportOTSurgeryKitCsv: (kitId: string) =>
    api.get<Blob>(`${base}/ot/surgery-kit/${kitId}/export-csv`, { responseType: "blob" }),
  exportOTSurgeryKitsCsvRange: (params: { from: string; to: string }) =>
    api.get<Blob>(`${base}/ot/surgery-kit/export-csv`, { params, responseType: "blob" }),
  createOTSurgeryConsumption: (body: {
    admissionId: string;
    surgeryDate: string;
    procedureName?: string;
    surgeonId?: string;
    notes?: string;
    items: Array<{ drugId: string; batchId?: string; quantity: number }>;
  }) => api.post<{ data: any }>(`${base}/ot/consumption`, body, { timeout: OT_LONG_RUNNING_MS }),
  createOTGeneralConsumption: (body: {
    reason: string;
    notes?: string;
    items: Array<{ drugId: string; batchId?: string; quantity: number }>;
  }) => api.post<{ data: any }>(`${base}/ot/general-consumption`, body, { timeout: OT_LONG_RUNNING_MS }),
  reconcileOT: (body: { date: string; notes?: string; approvedById?: string; lines: Array<{ drugId: string; physicalQty: number; reason?: string }> }) =>
    api.post<{ data: any }>(`${base}/ot/reconcile`, body, { timeout: OT_LONG_RUNNING_MS }),
  otVarianceReport: (params?: { from?: string; to?: string }) =>
    api.get<{ data: any[] }>(`${base}/ot/reports/variance`, { params }),
  otSurgeryReport: (params?: { from?: string; to?: string }) =>
    api.get<{ data: any[] }>(`${base}/ot/reports/surgery`, { params }),
  otDoctorReport: (params?: { from?: string; to?: string }) =>
    api.get<{ data: any[] }>(`${base}/ot/reports/doctor`, { params }),

  walkInSale: (body: {
    patientId: string;
    billDate?: string;
    recommendedDoctorId?: string | null;
    externalDoctor?: boolean;
    items: { drugId: string; quantity: number; discountAmount?: number }[];
  }) =>
    api.post<{ data: unknown }>(`${base}/walk-in-sale`, body),
};
